import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { useSidebarState } from '../hooks/useSidebarState';
import { Check, CreditCard, Loader2, Zap, Mail, Coins, CheckCircle, Building2, Users, Package, Clock, Tag, ArrowRight, ChevronRight, Sparkles, Shield, BarChart3, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../lib/apiClient';

const BILLING_TABS = [
  { id: 'plans', label: 'Plans', icon: CreditCard },
  { id: 'credits', label: 'Credits & Seats', icon: Coins },
  { id: 'subscription', label: 'Subscription', icon: BarChart3 },
  { id: 'invoices', label: 'Invoices', icon: Clock },
];

const Billing = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const sidebarCollapsed = useSidebarState();
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [usage, setUsage] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [purchasingPack, setPurchasingPack] = useState(null);
  const [creditPacks, setCreditPacks] = useState([]);
  const [checkingCreditPayment, setCheckingCreditPayment] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [purchasingSeats, setPurchasingSeats] = useState(false);
  const [seatQuantity, setSeatQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('plans');

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState('');

  // Upgrade confirmation modal
  const [upgradeModal, setUpgradeModal] = useState(null);

  useEffect(() => {
    fetchPricing();
    fetchUsage();
    fetchOrgData();

    const sessionId = searchParams.get('session_id');
    const creditSessionId = searchParams.get('credit_session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (creditSessionId && status === 'success') {
      pollCreditPaymentStatus(creditSessionId);
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled');
    }
  }, [searchParams]);

  const pollCreditPaymentStatus = async (sessionId) => {
    setCheckingCreditPayment(true);
    let attempts = 0;
    const maxAttempts = 10;
    const checkStatus = async () => {
      try {
        const data = await apiGet(`/api/org/credits/purchase-status/${sessionId}`);
        if (data.payment_status === 'paid') {
          toast.success(`Credit pack purchased! ${data.credits?.toLocaleString()} credits added.`);
          setCheckingCreditPayment(false);
          fetchOrgData();
          fetchUsage();
          window.history.replaceState({}, '', '/billing');
          return;
        }
      } catch { /* ignore poll error */ }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(checkStatus, 2000);
      } else {
        toast.error('Could not verify payment. Please refresh the page.');
        setCheckingCreditPayment(false);
      }
    };
    checkStatus();
  };

  const handlePurchaseCreditPack = async (packId) => {
    setPurchasingPack(packId);
    try {
      const data = await apiPost('/api/org/credits/purchase', {
        pack_id: packId, origin_url: window.location.origin
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.message || 'Failed to initiate purchase');
    } finally {
      setPurchasingPack(null);
    }
  };

  const handlePurchaseSeats = async () => {
    setPurchasingSeats(true);
    try {
      const data = await apiPost('/api/org/seats/purchase', {
        quantity: seatQuantity, origin_url: window.location.origin
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(err?.message || 'Failed to initiate seat purchase'); }
    finally { setPurchasingSeats(false); }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will be downgraded to the Free plan.')) return;
    try {
      await apiPost('/api/org/billing/cancel');
      toast.success('Subscription cancelled');
      fetchOrgData();
      fetchUsage();
      fetchPricing();
    } catch (err) { toast.error(err?.message || 'Failed to cancel subscription'); }
  };

  const fetchOrgData = async () => {
    try {
      const [orgData, packsData, invoicesData, subData] = await Promise.all([
        apiGet('/api/org').catch(() => null),
        apiGet('/api/org/credit-packs').catch(() => null),
        apiGet('/api/org/billing/invoices').catch(() => null),
        apiGet('/api/org/billing/subscription').catch(() => null),
      ]);
      if (orgData) setOrgData(orgData);
      if (packsData) setCreditPacks(packsData.packs || []);
      if (invoicesData) setInvoices(invoicesData.invoices || []);
      if (subData) setSubscription(subData);
    } catch { /* ignore */ }
  };

  const fetchPricing = async () => {
    try {
      const data = await apiGet('/api/pricing', { auth: false });
      setPricingTiers(data.tiers || []);
    } catch {
      console.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const data = await apiGet('/api/usage');
      setUsage(data);
    } catch {
      console.error('Failed to fetch usage');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email.');
      return;
    }
    setCheckingPayment(true);
    try {
      const data = await apiGet(`/api/payments/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPaymentStatus({ success: true, tier: data.tier });
        toast.success(`Successfully upgraded to ${data.tier}!`);
        const updatedUser = { ...user, subscription_tier: data.tier };
        localStorage.setItem('decoded_user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        fetchUsage();
        window.history.replaceState({}, '', '/billing');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ success: false, message: 'Payment session expired' });
        toast.error('Payment session expired');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch {
      toast.error('Error checking payment status');
    } finally {
      if (attempts === maxAttempts - 1 || paymentStatus) {
        setCheckingPayment(false);
      }
    }
  };

  // Promo code validation
  const validatePromo = useCallback(async (tierIdForPromo) => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoError('');
    setPromoResult(null);
    try {
      const data = await apiPost(`/api/validate-promo?code=${encodeURIComponent(promoCode)}&tier=${tierIdForPromo}`);
      setPromoResult(data);
    } catch (err) {
      setPromoError(err?.message || 'Invalid promo code');
    } finally {
      setPromoValidating(false);
    }
  }, [promoCode]);

  const handleUpgrade = async (tierId) => {
    if (tierId === 'enterprise') {
      window.location.href = 'mailto:sales@decoded.com?subject=Enterprise%20Inquiry';
      return;
    }
    if (tierId === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }
    // Open confirmation modal
    const tier = pricingTiers.find(t => t.id === tierId);
    setUpgradeModal(tier);
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  const confirmUpgrade = async () => {
    if (!upgradeModal) return;
    const tierId = upgradeModal.id;
    setUpgrading(tierId);
    setUpgradeModal(null);
    try {
      const data = await apiPost('/api/payments/checkout', {
        package_id: tierId,
        origin_url: window.location.origin,
        promo_code: promoResult?.code || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.message || 'Upgrade failed. Please try again.');
    } finally {
      setUpgrading(null);
    }
  };

  const currentTier = user?.subscription_tier || 'free';
  const tierOrder = { free: 0, starter: 1, growth: 2, scale: 3, enterprise: 4 };

  return (
    <div className="min-h-screen bg-[#F7F9FC] flex">
      <Sidebar user={user} setUser={setUser} activePage="billing" />

      <main className="flex-1 p-6 lg:p-8 lg:pt-24 transition-all duration-300">
        <div className="max-w-6xl">
          {/* Payment Status Banners */}
          {checkingPayment && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="payment-checking-banner">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <span className="text-blue-800">Verifying your payment...</span>
            </div>
          )}
          {paymentStatus?.success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="payment-success-banner">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800">
                Payment successful! You&apos;ve been upgraded to <strong className="capitalize">{paymentStatus.tier}</strong> plan.
              </span>
            </div>
          )}

          {/* Header */}
          <div className="flex items-end justify-between mb-6">
            <div>
              <h1 className="font-outfit text-3xl font-bold text-[#0B3D91]" data-testid="billing-title">
                Billing & Plans
              </h1>
              <p className="text-slate-500 text-sm mt-1">Manage your subscription, credits, and billing</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 bg-[#0B3D91]/8 rounded-lg">
                <span className="text-xs text-slate-500">Current Plan</span>
                <span className="block text-sm font-bold text-[#0B3D91] capitalize">{currentTier}</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200 shadow-sm mb-6" data-testid="billing-tabs">
            {BILLING_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                    isActive
                      ? 'bg-[#0B3D91] text-white shadow-md'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`billing-tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ===== PLANS TAB ===== */}
          {activeTab === 'plans' && (
            <div className="space-y-8 animate-in fade-in duration-300">
              {/* Self-serve plans */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-10 h-10 text-[#1E5EFF] animate-spin" />
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
                  {pricingTiers.filter(t => t.id !== 'enterprise').map((tier) => {
                    const isCurrentPlan = currentTier === tier.id;
                    const isHighlighted = tier.id === 'growth';
                    const isScale = tier.id === 'scale';
                    const canUpgrade = tierOrder[tier.id] > tierOrder[currentTier];
                    const canDowngrade = tierOrder[tier.id] < tierOrder[currentTier] && tier.id === 'free';

                    return (
                      <div
                        key={tier.id}
                        className={`rounded-2xl p-5 flex flex-col relative overflow-hidden transition-all hover:shadow-lg ${
                          isScale
                            ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 text-white shadow-xl'
                            : isHighlighted
                              ? 'bg-[#0B3D91] text-white shadow-[0_20px_60px_-15px_rgba(11,61,145,0.4)]'
                              : 'bg-white border border-slate-200 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]'
                        } ${isCurrentPlan ? 'ring-2 ring-[#1E5EFF]' : ''}`}
                        data-testid={`billing-tier-${tier.id}`}
                      >
                        {isScale && (
                          <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                        )}
                        {isHighlighted && (
                          <div className="inline-flex items-center gap-1 bg-white text-[#0B3D91] text-xs font-semibold px-2.5 py-1 rounded-full mb-3 self-start z-10">
                            <Zap className="w-3 h-3" />
                            POPULAR
                          </div>
                        )}
                        {isScale && (
                          <div className="inline-flex items-center gap-1 bg-indigo-500/30 text-indigo-200 text-xs font-semibold px-2.5 py-1 rounded-full mb-3 self-start z-10 backdrop-blur-sm">
                            <Sparkles className="w-3 h-3" />
                            MID-MARKET
                          </div>
                        )}
                        {isCurrentPlan && !isHighlighted && !isScale && (
                          <div className="inline-block bg-[#10B981] text-white text-xs font-semibold px-2.5 py-1 rounded-full mb-3 self-start">
                            CURRENT
                          </div>
                        )}

                        <h3 className={`font-outfit text-base font-semibold mb-1 ${isHighlighted || isScale ? 'text-white' : 'text-[#0B3D91]'}`}>
                          {tier.name}
                        </h3>
                        <div className="mb-3">
                          <span className={`text-2xl font-outfit font-bold ${isHighlighted || isScale ? 'text-white' : 'text-[#0B3D91]'}`}>
                            {tier.price === 0 ? 'Free' : tier.price === -1 ? 'Custom' : `$${tier.price}`}
                          </span>
                          {tier.price > 0 && (
                            <span className={`text-xs ${isHighlighted || isScale ? 'text-blue-200' : 'text-slate-500'}`}>/mo</span>
                          )}
                        </div>
                        <div className={`text-xs font-medium mb-3 px-2 py-1 rounded-md inline-block self-start ${
                          isHighlighted || isScale ? 'bg-white/10 text-blue-100' : 'bg-blue-50 text-blue-700'
                        }`}>
                          {tier.credits === -1 ? 'Custom' : `${tier.credits.toLocaleString()} credits/mo`}
                        </div>

                        <ul className="space-y-1.5 mb-4 flex-1">
                          {tier.features.slice(0, 5).map((feature, index) => (
                            <li key={index} className="flex items-start gap-1.5">
                              <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isHighlighted || isScale ? 'text-[#10B981]' : 'text-[#10B981]'}`} />
                              <span className={`text-xs leading-tight ${isHighlighted || isScale ? 'text-blue-100' : 'text-slate-600'}`}>
                                {feature}
                              </span>
                            </li>
                          ))}
                          {tier.features.length > 5 && (
                            <li className={`text-xs pl-5 ${isHighlighted || isScale ? 'text-blue-200' : 'text-slate-400'}`}>
                              +{tier.features.length - 5} more
                            </li>
                          )}
                        </ul>

                        <Button
                          onClick={() => {
                            if (!isCurrentPlan && (canUpgrade || canDowngrade)) handleUpgrade(tier.id);
                          }}
                          disabled={isCurrentPlan || upgrading === tier.id || (!canUpgrade && !canDowngrade)}
                          className={`w-full rounded-full py-3 text-xs font-semibold mt-auto ${
                            isCurrentPlan
                              ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                              : isScale
                                ? 'bg-white text-slate-900 hover:bg-slate-100'
                                : isHighlighted
                                  ? 'bg-white text-[#0B3D91] hover:bg-blue-50'
                                  : canUpgrade
                                    ? 'bg-[#0B3D91] text-white hover:bg-[#082A66]'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                          }`}
                          data-testid={`upgrade-btn-${tier.id}`}
                        >
                          {upgrading === tier.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isCurrentPlan ? (
                            'Current Plan'
                          ) : canUpgrade ? (
                            <span className="flex items-center gap-1 justify-center">Upgrade <ArrowRight className="w-3 h-3" /></span>
                          ) : canDowngrade ? (
                            'Downgrade'
                          ) : (
                            'N/A'
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Enterprise Section */}
              {!loading && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="font-outfit text-lg font-bold text-[#0B3D91]" data-testid="enterprise-plans-title">Enterprise Plans</h2>
                      <p className="text-xs text-slate-500">For organizations requiring full visibility, control, and optimization.</p>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    {[
                      {
                        name: "Enterprise Starter",
                        price: "\u20AC60,000 \u2013 \u20AC90,000",
                        period: "/year",
                        tagline: "Enterprise-grade tariff visibility",
                        features: ["Bulk HS classification", "Master data ingestion", "Misclassification detection", "Financial impact estimation", "Exportable reports", "Basic AI insights"],
                        badge: null
                      },
                      {
                        name: "Enterprise Monitor",
                        price: "\u20AC100,000 \u2013 \u20AC150,000",
                        period: "/year",
                        tagline: "Continuous tariff intelligence",
                        features: ["Everything in Starter, plus:", "Continuous monitoring", "Real-time alerts", "Full historical scanning", "AI analyst", "Landed cost calculator"],
                        badge: "Most Popular"
                      },
                      {
                        name: "Enterprise Scale",
                        price: "\u20AC200,000 \u2013 \u20AC350,000+",
                        period: "/year",
                        tagline: "Global trade intelligence",
                        features: ["Everything in Monitor, plus:", "Multi-country & multi-entity", "Near-unlimited processing", "ERP integrations (SAP)", "API access", "Dedicated onboarding"],
                        badge: "Global Enterprises"
                      }
                    ].map((tier, i) => {
                      const isPopular = tier.badge === 'Most Popular';
                      return (
                        <div
                          key={i}
                          className={`rounded-2xl p-5 flex flex-col ${
                            isPopular ? 'bg-slate-900 text-white shadow-lg' : 'bg-white border border-slate-200 shadow-sm'
                          }`}
                          data-testid={`billing-enterprise-${i}`}
                        >
                          {tier.badge && (
                            <span className={`inline-flex self-start text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${
                              isPopular ? 'bg-white/15 text-white' : 'bg-blue-50 text-blue-700'
                            }`}>{tier.badge}</span>
                          )}
                          <h3 className={`font-outfit text-base font-semibold mb-1 ${isPopular ? 'text-white' : 'text-[#0B3D91]'}`}>{tier.name}</h3>
                          <p className={`text-xs mb-2 ${isPopular ? 'text-slate-300' : 'text-slate-500'}`}>{tier.tagline}</p>
                          <div className="mb-3">
                            <span className={`text-lg font-outfit font-bold ${isPopular ? 'text-white' : 'text-[#0B3D91]'}`}>{tier.price}</span>
                            <span className={`text-xs ${isPopular ? 'text-slate-400' : 'text-slate-500'}`}>{tier.period}</span>
                          </div>
                          <ul className="space-y-1.5 mb-4 flex-1">
                            {tier.features.map((f, fi) => (
                              <li key={fi} className="flex items-start gap-1.5">
                                <Check className={`w-3 h-3 mt-0.5 flex-shrink-0 ${isPopular ? 'text-emerald-400' : 'text-[#10B981]'}`} />
                                <span className={`text-xs ${isPopular ? 'text-slate-200' : 'text-slate-600'}`}>{f}</span>
                              </li>
                            ))}
                          </ul>
                          <Button
                            onClick={() => window.location.href = 'mailto:sales@decoded.com?subject=Enterprise%20Inquiry'}
                            className={`w-full rounded-full py-3 text-xs font-semibold mt-auto ${
                              isPopular ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-[#0B3D91] text-white hover:bg-[#082A66]'
                            }`}
                            data-testid={`billing-enterprise-cta-${i}`}
                          >
                            <Mail className="w-3.5 h-3.5 mr-1.5" /> Contact Sales
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== CREDITS & SEATS TAB ===== */}
          {activeTab === 'credits' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {/* Org Credit Pool */}
              {orgData && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-[#0B3D91]" />
                      <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]" data-testid="org-credits-title">Organization Credit Pool</h2>
                    </div>
                    <span className="text-sm text-slate-500 capitalize">{orgData.name}</span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4" data-testid="monthly-credits-card">
                      <p className="text-sm text-slate-500 mb-1">Monthly Credits</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {orgData.credits?.monthly_remaining?.toLocaleString()}
                        <span className="text-sm font-normal text-slate-400"> / {orgData.credits?.monthly_limit === -1 ? '\u221E' : orgData.credits?.monthly_limit?.toLocaleString()}</span>
                      </p>
                      {orgData.credits?.monthly_limit > 0 && (
                        <div className="mt-2 h-2 bg-blue-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min((orgData.credits.monthly_used / orgData.credits.monthly_limit) * 100, 100)}%` }} />
                        </div>
                      )}
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4" data-testid="purchased-credits-card">
                      <p className="text-sm text-slate-500 mb-1">Purchased Credits</p>
                      <p className="text-2xl font-bold text-emerald-700">{orgData.credits?.purchased_remaining?.toLocaleString() || 0}</p>
                      <p className="text-xs text-slate-400 mt-1">Expire 180 days after purchase</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4" data-testid="total-credits-card">
                      <p className="text-sm text-slate-500 mb-1">Total Available</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {orgData.credits?.total_available === -1 ? '\u221E' : orgData.credits?.total_available?.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Monthly consumed first</p>
                    </div>
                  </div>

                  {/* Seats Info */}
                  <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">
                        <strong>{orgData.seats?.used_seats}</strong> of <strong>{orgData.seats?.total_seats}</strong> seats used
                      </span>
                    </div>
                    {orgData.seats?.additional_seats > 0 && (
                      <span className="text-xs text-slate-400">
                        ({orgData.seats.included_seats} included + {orgData.seats.additional_seats} additional @ ${orgData.seats.seat_price}/mo)
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Credit Packs */}
              {creditPacks.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-2">
                    <Package className="w-5 h-5 text-[#0B3D91]" />
                    <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]" data-testid="credit-packs-title">Credit Packs</h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Purchase additional credits. Expire 180 days after purchase.</p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {creditPacks.map((pack) => (
                      <div key={pack.id} className="border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all" data-testid={`credit-pack-${pack.id}`}>
                        <h3 className="font-semibold text-slate-900 mb-1">{pack.name}</h3>
                        <p className="text-3xl font-bold text-[#0B3D91] mb-1">{pack.credits.toLocaleString()}</p>
                        <p className="text-sm text-slate-500 mb-4">credits</p>
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-slate-900">${pack.price}</span>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" disabled={!!purchasingPack || checkingCreditPayment || !orgData?.has_billing} onClick={() => handlePurchaseCreditPack(pack.id)} data-testid={`buy-pack-${pack.id}`}>
                            {purchasingPack === pack.id ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />...</> : 'Buy Pack'}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">${(pack.price / pack.credits * 1000).toFixed(2)} per 1,000 credits</p>
                      </div>
                    ))}
                  </div>
                  {!orgData?.has_billing && (
                    <p className="text-xs text-amber-600 mt-3">You need billing permission to purchase credit packs.</p>
                  )}
                </div>
              )}

              {/* Seat Management */}
              {orgData && orgData.has_billing && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="seat-management-section">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-[#0B3D91]" />
                    <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">Add Seats</h2>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Purchase additional seats at ${orgData.seats?.seat_price}/seat/month.</p>
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-600 font-medium">Quantity:</label>
                      <select value={seatQuantity} onChange={(e) => setSeatQuantity(parseInt(e.target.value))} className="border rounded-lg px-3 py-2 text-sm" data-testid="seat-quantity-select">
                        {[1, 2, 3, 5, 10, 20].map(n => (
                          <option key={n} value={n}>{n} seat{n > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-sm text-slate-600">
                      Total: <strong className="text-slate-900">${(seatQuantity * (orgData.seats?.seat_price || 9.99)).toFixed(2)}</strong>/month
                    </div>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 ml-auto" onClick={handlePurchaseSeats} disabled={purchasingSeats} data-testid="purchase-seats-btn">
                      {purchasingSeats ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />...</> : 'Purchase Seats'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Usage Stats */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <h2 className="font-outfit text-lg font-semibold text-[#0B3D91] mb-4">Usage This Month</h2>
                <div className="bg-gradient-to-r from-[#0B3D91]/5 to-[#06B6D4]/5 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Coins className="w-5 h-5 text-[#0B3D91]" />
                      <span className="font-medium text-[#0B3D91]">AI Credits</span>
                    </div>
                    <span className="text-sm text-slate-500">
                      Model: <span className="font-medium text-slate-700 capitalize">{usage?.preferred_model?.replace('-', ' ') || 'Gemini Flash'}</span>
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-outfit text-3xl font-bold text-[#0B3D91]">{usage?.credits_used?.toLocaleString() || 0}</span>
                    <span className="text-slate-400">{usage?.credits_limit === -1 ? '/ \u221E' : `/ ${usage?.credits_limit?.toLocaleString() || '\u2014'}`}</span>
                  </div>
                  {usage?.credits_limit && usage.credits_limit !== -1 && (
                    <div className="mt-2">
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#0B3D91] to-[#06B6D4] rounded-full transition-all" style={{ width: `${Math.min((usage.credits_used / usage.credits_limit) * 100, 100)}%` }} />
                      </div>
                    </div>
                  )}
                  {usage?.enterprise_plan && (
                    <p className="text-xs text-purple-600 mt-2" data-testid="enterprise-plan-info">
                      Plan: {usage.enterprise_plan.name} (${usage.enterprise_plan.price_monthly?.toLocaleString()}/mo)
                    </p>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { label: 'Single Analyses', key: 'single_analysis' },
                    { label: 'Bulk Products', key: 'bulk_products' },
                    { label: 'Sourcing Queries', key: 'sourcing' },
                    { label: 'Origin Validations', key: 'origin' },
                  ].map(({ label, key }) => (
                    <div key={key}>
                      <p className="text-sm text-slate-500 mb-1">{label}</p>
                      <p className="font-outfit text-2xl font-bold text-[#0B3D91]">
                        {usage?.[key] || 0}
                        <span className="text-sm font-normal text-slate-400">
                          {usage?.limits?.[key] === -1 ? ' / \u221E' : ` / ${usage?.limits?.[key] || '\u2014'}`}
                        </span>
                      </p>
                    </div>
                  ))}
                </div>
                {usage?.resets_at && (
                  <p className="text-xs text-slate-400 mt-4">Usage resets on {new Date(usage.resets_at).toLocaleDateString()}</p>
                )}
              </div>
            </div>
          )}

          {/* ===== SUBSCRIPTION TAB ===== */}
          {activeTab === 'subscription' && (
            <div className="space-y-6 animate-in fade-in duration-300">
              {subscription && orgData?.has_billing ? (
                <>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="subscription-management-section">
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="w-5 h-5 text-[#0B3D91]" />
                      <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">Subscription Details</h2>
                    </div>
                    <div className="grid sm:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Plan</p>
                        <p className="text-lg font-bold text-slate-900 capitalize">{subscription.plan_name}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Monthly Cost</p>
                        <p className="text-lg font-bold text-slate-900">
                          ${subscription.total_monthly?.toFixed(2)}
                          {subscription.monthly_seat_cost > 0 && (
                            <span className="text-xs font-normal text-slate-400 block">
                              ${subscription.plan_price} plan + ${subscription.monthly_seat_cost?.toFixed(2)} seats
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Status</p>
                        <p className="text-lg font-bold text-green-600 capitalize">{subscription.status}</p>
                      </div>
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs text-slate-500 mb-1">Seats</p>
                        <p className="text-lg font-bold text-slate-900">
                          {subscription.included_seats + (subscription.additional_seats || 0)}
                          <span className="text-xs font-normal text-slate-400 block">
                            {subscription.included_seats} included{subscription.additional_seats > 0 ? ` + ${subscription.additional_seats} extra` : ''}
                          </span>
                        </p>
                      </div>
                    </div>
                    {subscription.plan_tier !== 'free' && (
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <p className="text-sm text-slate-500">Need to make changes to your subscription?</p>
                        <div className="flex gap-3">
                          <Button variant="outline" size="sm" onClick={() => setActiveTab('plans')} data-testid="change-plan-btn">
                            Change Plan
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancelSubscription} data-testid="cancel-subscription-btn">
                            Cancel Subscription
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {subscription.plan_tier === 'free' && (
                    <div className="bg-gradient-to-r from-[#0B3D91] to-indigo-700 rounded-2xl p-6 text-white">
                      <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="w-5 h-5" />
                        <h3 className="font-outfit text-lg font-semibold">Upgrade Your Plan</h3>
                      </div>
                      <p className="text-blue-100 text-sm mb-4">Get more credits, unlimited analyses, and team collaboration features.</p>
                      <Button className="bg-white text-[#0B3D91] hover:bg-blue-50" onClick={() => setActiveTab('plans')} data-testid="upgrade-prompt-btn">
                        View Plans <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-100">
                  <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-outfit text-lg font-semibold text-slate-700 mb-1">No Active Subscription</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {!orgData?.has_billing
                      ? 'You need billing permission to manage subscriptions.'
                      : 'Choose a plan to get started with more features.'}
                  </p>
                  <Button className="bg-[#0B3D91] text-white hover:bg-[#082A66]" onClick={() => setActiveTab('plans')}>
                    View Plans
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ===== INVOICES TAB ===== */}
          {activeTab === 'invoices' && (
            <div className="animate-in fade-in duration-300">
              {invoices.length > 0 ? (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="invoices-section">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-5 h-5 text-[#0B3D91]" />
                    <h2 className="font-outfit text-lg font-semibold text-[#0B3D91]">Payment History</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100">
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-xs">Description</th>
                          <th className="text-left py-3 px-2 text-slate-500 font-medium text-xs">Date</th>
                          <th className="text-right py-3 px-2 text-slate-500 font-medium text-xs">Amount</th>
                          <th className="text-right py-3 px-2 text-slate-500 font-medium text-xs">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice, i) => (
                          <tr key={invoice.id || i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`invoice-row-${i}`}>
                            <td className="py-3 px-2 font-medium text-slate-900">{invoice.description}</td>
                            <td className="py-3 px-2 text-slate-500">{new Date(invoice.completed_at || invoice.created_at).toLocaleDateString()}</td>
                            <td className="py-3 px-2 text-right font-medium text-slate-900">${invoice.amount?.toFixed(2)}</td>
                            <td className="py-3 px-2 text-right">
                              <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700 capitalize">{invoice.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-100">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-outfit text-lg font-semibold text-slate-700 mb-1">No Invoices Yet</h3>
                  <p className="text-sm text-slate-500">Your payment history will appear here once you make a purchase.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* ===== UPGRADE CONFIRMATION MODAL ===== */}
      {upgradeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="upgrade-modal-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" data-testid="upgrade-modal">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0B3D91] to-indigo-700 p-5 text-white relative">
              <button
                onClick={() => { setUpgradeModal(null); setPromoResult(null); setPromoError(''); setPromoCode(''); }}
                className="absolute top-4 right-4 text-white/70 hover:text-white"
                data-testid="upgrade-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-medium text-blue-200">Upgrade to</span>
              </div>
              <h3 className="font-outfit text-2xl font-bold">{upgradeModal.name} Plan</h3>
            </div>

            <div className="p-5 space-y-4">
              {/* Plan comparison */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-0.5">Current</p>
                  <p className="font-bold text-slate-900 capitalize">{currentTier}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#0B3D91]" />
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-0.5">New</p>
                  <p className="font-bold text-[#0B3D91]">{upgradeModal.name}</p>
                </div>
              </div>

              {/* What you get */}
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">What you get</p>
                <ul className="space-y-1.5">
                  {upgradeModal.features?.slice(0, 4).map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Promo Code Input */}
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide block mb-2">Promo Code</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); setPromoResult(null); }}
                      className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B3D91]/20 focus:border-[#0B3D91] outline-none"
                      data-testid="promo-code-input"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-4"
                    disabled={!promoCode.trim() || promoValidating}
                    onClick={() => validatePromo(upgradeModal.id)}
                    data-testid="promo-validate-btn"
                  >
                    {promoValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
                {promoResult && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg" data-testid="promo-success">
                    <CheckCircle className="w-4 h-4" />
                    {promoResult.discount_percent}% discount applied!
                  </div>
                )}
                {promoError && (
                  <p className="mt-2 text-xs text-red-600" data-testid="promo-error">{promoError}</p>
                )}
              </div>

              {/* Price */}
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">
                    {promoResult ? (
                      <span>
                        <span className="line-through text-slate-400">${upgradeModal.price}</span>
                        <span className="ml-2 font-bold text-[#0B3D91]">${(upgradeModal.price * (1 - promoResult.discount_percent / 100)).toFixed(2)}</span>
                      </span>
                    ) : (
                      <span className="font-bold text-[#0B3D91] text-xl">${upgradeModal.price}</span>
                    )}
                    <span className="text-slate-400 text-sm">/month</span>
                  </span>
                  {promoResult && (
                    <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full">
                      Save ${(upgradeModal.price * promoResult.discount_percent / 100).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setUpgradeModal(null); setPromoResult(null); setPromoError(''); setPromoCode(''); }}
                  data-testid="upgrade-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-[#0B3D91] text-white hover:bg-[#082A66]"
                  onClick={confirmUpgrade}
                  disabled={upgrading}
                  data-testid="upgrade-confirm-btn"
                >
                  {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="flex items-center gap-1">
                      <Shield className="w-4 h-4" /> Proceed to Checkout
                    </span>
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-400 text-center">Secure payment via Stripe. Cancel anytime.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
